import { useState } from "react";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { User, Mail, Shield, Lock, Loader2, Eye, EyeOff, Check } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { passwordStrength } from "@/utils/passwordStrength";

export default function ProfilePage() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changing, setChanging] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    const s = passwordStrength(newPassword);
    if (!s.meets) {
      toast.error(s.hint || "Password is too weak");
      return;
    }

    setChanging(true);
    try {
      // Backend route is POST (used to call PUT — which 405'd).
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword || undefined,
        new_password: newPassword,
      }, { withCredentials: true });
      
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChanging(false);
    }
  };

  const isGoogleUser = user?.auth_provider === "google";
  const isInvitedUser = user?.auth_provider === "invited";

  return (
    <div className="page-container max-w-2xl" data-testid="profile-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Home</span><span>/</span><span className="text-gray-900">Profile</span>
      </div>

      <h1 className="text-2xl font-bold font-['Manrope'] mb-6">Profile Settings</h1>

      {/* User Info Card */}
      <Card className="border-gray-200 shadow-none mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{user?.name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Mail className="w-4 h-4" />
                {user?.email}
              </div>
              {user?.role === "admin" && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mt-2">
                  <Shield className="w-3 h-3" />Admin
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Account Type</p>
              <p className="font-medium capitalize">{user?.auth_provider || "Local"}</p>
            </div>
            <div>
              <p className="text-gray-500">Member Since</p>
              <p className="font-medium">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card — hidden for Google-OAuth users (they manage
          credentials at their Google account). Per product call: Google
          accounts have no app-managed password and no "set additional
          password" flow. */}
      {!isGoogleUser && (
      <Card className="border-gray-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Change Password
          </CardTitle>
          <CardDescription>
            {isInvitedUser
              ? "Change your temporary password to something secure."
              : "Update your account password."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {/* Current password */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Current Password
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                New Password
              </label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 chars, letters + numbers)"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && (
                <div className="mt-2">
                  <PasswordStrengthMeter value={newPassword} testid="profile-pwd-strength" />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword && (
                <div className="flex items-center gap-1 mt-1.5">
                  {confirmPassword === newPassword ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Passwords match
                    </span>
                  ) : (
                    <span className="text-xs text-red-500">Passwords don't match</span>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={changing || !newPassword || !confirmPassword || newPassword !== confirmPassword || !passwordStrength(newPassword).meets}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="change-password-btn"
            >
              {changing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
